import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase'; 
import { doc, collection, addDoc, deleteDoc, updateDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function PropietarioReservas({
  usuarioLogueado,
  areasSociales,
  reservas,
  formReserva,
  setFormReserva,
  abrirArchivoConLimiteGlobal
}) {
  const [subiendoVoucherReserva, setSubiendoVoucherReserva] = useState(false);
  const [archivoVoucherReserva, setArchivoVoucherReserva] = useState(null);
  const [reservasLocales, setReservasLocales] = useState(reservas);

  // ⏰ Estado para múltiples horarios seleccionados
  const [horariosSeleccionados, setHorariosSeleccionados] = useState([]);

  // 🧹 Estados para la gestión de limpieza dinámica cuando el área es "Libre"
  const [tipoLimpiezaElegida, setTipoLimpiezaElegida] = useState('Lo realiza el propietario');
  const [nombreLimpiezaExterna, setNombreLimpiezaExterna] = useState('');
  const [dniLimpiezaExterna, setDniLimpiezaExterna] = useState('');

  // 📄 Estados para la Declaración Jurada, Nota Importante, PDF de Nota y Plazo de Anticipación
  const [declaracionJuradaUrl, setDeclaracionJuradaUrl] = useState('');
  const [aceptoDeclaracion, setAceptoDeclaracion] = useState(false);
  const [notaImportante, setNotaImportante] = useState('');
  const [notaImportantePdfUrl, setNotaImportantePdfUrl] = useState('');
  const [plazoAnticipacionMeses, setPlazoAnticipacionMeses] = useState('');

  // 🔄 Estado para gestionar la subida de vouchers de garantía extra por reserva
  const [archivosGarantia, setArchivosGarantia] = useState({});

  const edificioKey = usuarioLogueado?.edificioId || usuarioLogueado?.edificio || 'general';

  useEffect(() => {
    setReservasLocales(reservas);
  }, [reservas]);

  // 🔍 Obtener la Declaración Jurada, la Nota Importante, el PDF y el Plazo del edificio al cargar el componente
  useEffect(() => {
    const obtenerDatosEdificio = async () => {
      if (!edificioKey) return;
      try {
        const edifSnap = await getDoc(doc(db, 'edificios_mmdl', edificioKey));
        if (edifSnap.exists()) {
          const data = edifSnap.data();
          if (data.declaracionJuradaUrl) {
            setDeclaracionJuradaUrl(data.declaracionJuradaUrl);
          }
          if (data.notaImportanteReservas) {
            setNotaImportante(data.notaImportanteReservas);
          }
          if (data.notaImportantePdfUrl) {
            setNotaImportantePdfUrl(data.notaImportantePdfUrl);
          }
          if (data.plazoAnticipacionMeses !== undefined && data.plazoAnticipacionMeses !== '') {
            setPlazoAnticipacionMeses(data.plazoAnticipacionMeses);
          }
        }
      } catch (error) {
        console.error("Error al obtener los datos del edificio:", error);
      }
    };
    obtenerDatosEdificio();
  }, [edificioKey]);

  // ⏳ Cálculo dinámico de la fecha máxima permitida usando 30 días por mes
  const obtenerFechaMaxima = () => {
    if (!plazoAnticipacionMeses || isNaN(plazoAnticipacionMeses)) return undefined;
    const meses = parseInt(plazoAnticipacionMeses, 10);
    const diasTotales = meses * 30;
    const fechaMax = new Date();
    fechaMax.setDate(fechaMax.getDate() + diasTotales);
    return fechaMax.toISOString().split('T')[0];
  };

  const fechaMaximaStr = obtenerFechaMaxima();

  // 🔄 Áreas sociales ordenadas alfabéticamente por nombre
  const areasSocialesOrdenadas = [...areasSociales].sort((a, b) => 
    (a.nombre || '').localeCompare(b.nombre || '')
  );

  const areaSeleccionadaObj = areasSociales.find(a => a.id === formReserva.areaId);

  // 🕒 Lógica inteligente para extraer las horas disponibles según la fecha elegida y los bloques por días
  const obtenerHorasDisponiblesArea = () => {
    if (!areaSeleccionadaObj) return ['09:00 - 22:00'];

    if (areaSeleccionadaObj.horariosPorDia && areaSeleccionadaObj.horariosPorDia.length > 0 && formReserva.fecha) {
      const [anio, mes, dia] = formReserva.fecha.split('-').map(Number);
      const fechaObj = new Date(anio, mes - 1, dia);
      const nombresDias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      const nombreDiaElegido = nombresDias[fechaObj.getDay()].toLowerCase();

      const bloqueEncontrado = areaSeleccionadaObj.horariosPorDia.find(bloque => {
        const diasTexto = bloque.diasText.toLowerCase();
        return diasTexto.includes(nombreDiaElegido);
      });

      if (bloqueEncontrado && bloqueEncontrado.hora) {
        if (bloqueEncontrado.hora.includes(',')) {
          return bloqueEncontrado.hora.split(',').map(h => h.trim());
        }
        return [bloqueEncontrado.hora];
      }
    }

    return areaSeleccionadaObj.horasList || areaSeleccionadaObj.horasDisponibles || ['09:00 - 22:00'];
  };

  const horasDisponiblesArea = obtenerHorasDisponiblesArea();

  const obtenerValorNumerico = (valorStr) => {
    if (valorStr === undefined || valorStr === null || valorStr === '') return 0;
    if (typeof valorStr === 'number') return valorStr;
    const limpio = valorStr.toString().replace(/[^0-9.]/g, '');
    const num = parseFloat(limpio);
    return isNaN(num) ? 0 : num;
  };

  const costoNum = obtenerValorNumerico(areaSeleccionadaObj?.costo);
  const garantiaNum = obtenerValorNumerico(areaSeleccionadaObj?.garantia);
  const esDePago = areaSeleccionadaObj && (costoNum > 0 || garantiaNum > 0);

  const manejarCambioFecha = (e) => {
    const fechaStr = e.target.value;
    setHorariosSeleccionados([]); 
    if (!fechaStr || !areaSeleccionadaObj) {
      setFormReserva({...formReserva, fecha: fechaStr});
      return;
    }

    const [anio, mes, dia] = fechaStr.split('-').map(Number);
    const fechaObj = new Date(anio, mes - 1, dia);
    const diaSemanaIndex = fechaObj.getDay();

    const nombresDias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const nombreDiaElegido = nombresDias[diaSemanaIndex];

    const diasPermitidos = areaSeleccionadaObj?.diasList || areaSeleccionadaObj?.diasDisponibles || ['Lunes a Domingo'];
    
    let esPermitido = true;
    if (areaSeleccionadaObj.horariosPorDia && areaSeleccionadaObj.horariosPorDia.length > 0) {
      esPermitido = areaSeleccionadaObj.horariosPorDia.some(bloque => 
        bloque.diasText.toLowerCase().includes(nombreDiaElegido.toLowerCase())
      );
    } else if (diasPermitidos.length > 0 && !diasPermitidos.includes('Lunes a Domingo')) {
      esPermitido = diasPermitidos.some(d => d.toLowerCase().includes(nombreDiaElegido.toLowerCase()));
    }

    if (!esPermitido) {
      alert(`⚠️ El área "${areaSeleccionadaObj.nombre}" no se encuentra habilitada para el día ${nombreDiaElegido} (${fechaStr}).`);
      setFormReserva({...formReserva, fecha: ''});
    } else {
      setFormReserva({...formReserva, fecha: fechaStr});
    }
  };

  // 🔒 Verificamos si ya existe alguna reserva en esta fecha para esta área
  const hayReservaEnFecha = reservasLocales.some(
    r => String(r.areaId) === String(formReserva.areaId) && r.fecha === formReserva.fecha
  );

  const horariosOcupados = reservasLocales
    .filter(r => String(r.areaId) === String(formReserva.areaId) && r.fecha === formReserva.fecha)
    .map(r => r.horario);

  // Si el área tiene activo el bloqueo de día completo y ya hay al menos una reserva, TODO el día se bloquea
  const estaFechaTotalmenteBloqueada = areaSeleccionadaObj?.bloqueoDiaCompleto && hayReservaEnFecha;

  const indicesSeleccionados = horariosSeleccionados
    .map(h => horasDisponiblesArea.indexOf(h))
    .filter(idx => idx !== -1)
    .sort((a, b) => a - b);

  let indicesEfectivos = [];
  if (indicesSeleccionados.length > 0) {
    const minIdx = indicesSeleccionados[0];
    const maxIdx = indicesSeleccionados[indicesSeleccionados.length - 1];
    for (let i = minIdx; i <= maxIdx; i++) {
      indicesEfectivos.push(i);
    }
  }
  const horariosEfectivos = indicesEfectivos.map(idx => horasDisponiblesArea[idx]);

  const manejarEnvioReserva = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (usuarioLogueado?.departamento?.bloqueadoReservas) {
      alert("❌ Sus reservas de áreas sociales se encuentran temporalmente suspendidas por cuotas de mantenimiento pendientes o morosidad. Por favor, regularice su situación con la administración.");
      return;
    }

    if (areasSociales.length === 0) {
      alert("❌ No hay áreas sociales habilitadas en este edificio para realizar reservas.");
      return;
    }

    if (!formReserva.areaId || !formReserva.fecha || horariosEfectivos.length === 0) {
      alert("⚠️ Por favor completa el área, la fecha y selecciona al menos un horario.");
      return;
    }

    if (estaFechaTotalmenteBloqueada) {
      alert("❌ Lo sentimos, esta fecha ya se encuentra completamente reservada.");
      return;
    }

    if (areaSeleccionadaObj?.limpieza === 'Libre' && tipoLimpiezaElegida === 'Limpieza externa contratada') {
      if (!nombreLimpiezaExterna.trim() || !dniLimpiezaExterna.trim()) {
        alert("⚠️ Por favor ingresa el nombre completo y el DNI de la persona externa contratada para la limpieza.");
        return;
      }
    }

    if (declaracionJuradaUrl && !aceptoDeclaracion) {
      alert("⚠️ Debes leer y aceptar la Declaración Jurada para poder confirmar tu reserva.");
      return;
    }

    const hayConflictoEnRango = horariosEfectivos.some(h => horariosOcupados.includes(h));
    if (hayConflictoEnRango) {
      alert("❌ Lo sentimos, hay uno o más horarios ocupados dentro del rango de horas que intentas reservar. Por favor revisa tu selección.");
      return;
    }

    if (esDePago && !archivoVoucherReserva) {
      alert("⚠️ Esta área social requiere un pago o garantía. Por favor, adjunta la foto o PDF del voucher de pago para continuar.");
      return;
    }

    try {
      setSubiendoVoucherReserva(true);
      let voucherUrlDescarga = '';
      const dptoKey = String(usuarioLogueado?.departamento?.departamento || 'general');
      const batchTimestamp = new Date().toISOString();

      if (archivoVoucherReserva) {
        const storageRef = ref(storage, `vouchers_reservas_${edificioKey}/dpto_${dptoKey}_${Date.now()}_${archivoVoucherReserva.name}`);
        await uploadBytes(storageRef, archivoVoucherReserva);
        voucherUrlDescarga = await getDownloadURL(storageRef);
      }

      let nuevasReservasAgregadas = [];

      for (let h of horariosEfectivos) {
        const nuevaReservaData = {
          areaId: formReserva.areaId,
          departamento: dptoKey,
          fecha: formReserva.fecha,
          horario: h, // 👈 Guarda el horario exacto elegido para que lo vea la administración
          voucherUrl: voucherUrlDescarga,
          estadoPago: esDePago ? 'En revisión por administración' : 'Gratis / Aprobado',
          fechaCreacion: batchTimestamp,
          tipoLimpieza: areaSeleccionadaObj?.limpieza === 'Libre' ? tipoLimpiezaElegida : (areaSeleccionadaObj?.limpieza || 'Limpieza incluida'),
          nombreLimpiezaExterna: areaSeleccionadaObj?.limpieza === 'Libre' && tipoLimpiezaElegida === 'Limpieza externa contratada' ? nombreLimpiezaExterna.trim() : '',
          dniLimpiezaExterna: areaSeleccionadaObj?.limpieza === 'Libre' && tipoLimpiezaElegida === 'Limpieza externa contratada' ? dniLimpiezaExterna.trim() : ''
        };

        const docRef = await addDoc(collection(db, `reservas_${edificioKey}`), nuevaReservaData);
        nuevasReservasAgregadas.push({ id: docRef.id, ...nuevaReservaData });
      }

      setReservasLocales(prev => [...nuevasReservasAgregadas, ...prev]);

      setFormReserva({ areaId: '', departamento: '', fecha: '' });
      setHorariosSeleccionados([]);
      setArchivoVoucherReserva(null);
      setAceptoDeclaracion(false);
      setNombreLimpiezaExterna('');
      setDniLimpiezaExterna('');
      alert("¡Reservas realizadas con éxito!");
    } catch (error) {
      console.error("Error al crear reservas con voucher:", error);
      alert("Hubo un error al procesar las reservas. Por favor, inténtalo de nuevo.");
    } finally {
      setSubiendoVoucherReserva(false);
    }
  };

  const manejarSubirVoucherGarantia = async (grupo) => {
    const archivo = archivosGarantia[grupo.key];
    if (!archivo) {
      alert("⚠️ Selecciona un archivo PDF o imagen para el voucher de garantía.");
      return;
    }

    try {
      const dptoKey = String(usuarioLogueado?.departamento?.departamento || 'general');
      const storageRef = ref(storage, `vouchers_garantia_${edificioKey}/dpto_${dptoKey}_${Date.now()}_${archivo.name}`);
      await uploadBytes(storageRef, archivo);
      const urlDescarga = await getDownloadURL(storageRef);

      await Promise.all(
        grupo.ids.map(id => 
          updateDoc(doc(db, `reservas_${edificioKey}`, id), {
            voucherGarantiaUrl: urlDescarga,
            estadoGarantia: 'En revisión por administración'
          })
        )
      );

      setReservasLocales(prev => prev.map(r => {
        if (grupo.ids.includes(r.id)) {
          return {
            ...r,
            voucherGarantiaUrl: urlDescarga,
            estadoGarantia: 'En revisión por administración'
          };
        }
        return r;
      }));

      setArchivosGarantia(prev => ({ ...prev, [grupo.key]: null }));
      alert("✅ ¡Voucher de garantía subido con éxito y pendiente de revisión!");
    } catch (error) {
      console.error("Error al subir voucher de garantía:", error);
      alert("❌ Hubo un error al subir el voucher de garantía. Inténtalo de nuevo.");
    }
  };

  const cancelarReservaGrupo = async (ids) => {
    if (!window.confirm("¿Estás seguro de que deseas cancelar esta reserva? Los horarios quedarán libres nuevamente.")) {
      return;
    }

    try {
      for (let id of ids) {
        await deleteDoc(doc(db, `reservas_${edificioKey}`, id));
      }
      setReservasLocales(prev => prev.filter(r => !ids.includes(r.id)));
      alert("🗑️ Reserva cancelada con éxito.");
    } catch (error) {
      console.error("Error al cancelar la reserva:", error);
      alert("Hubo un error al intentar cancelar la reserva. Por favor, inténtalo de nuevo.");
    }
  };

  const miDepartamentoStr = String(usuarioLogueado?.departamento?.departamento || '');
  const misReservas = reservasLocales.filter(r => String(r.departamento) === miDepartamentoStr);

  const reservasAgrupadasMap = {};
  misReservas.forEach(r => {
    const key = `${r.areaId}_${r.fecha}_${r.voucherUrl || ''}_${r.fechaCreacion || r.id}`;
    if (!reservasAgrupadasMap[key]) {
      reservasAgrupadasMap[key] = {
        key,
        ids: [r.id],
        areaId: r.areaId,
        fecha: r.fecha,
        horarios: [r.horario],
        voucherUrl: r.voucherUrl,
        voucherGarantiaUrl: r.voucherGarantiaUrl,
        estadoPago: r.estadoPago,
        estadoGarantia: r.estadoGarantia,
        tipoLimpieza: r.tipoLimpieza,
        nombreLimpiezaExterna: r.nombreLimpiezaExterna,
        dniLimpiezaExterna: r.dniLimpiezaExterna
      };
    } else {
      reservasAgrupadasMap[key].ids.push(r.id);
      reservasAgrupadasMap[key].horarios.push(r.horario);
      if (r.voucherGarantiaUrl && !reservasAgrupadasMap[key].voucherGarantiaUrl) {
        reservasAgrupadasMap[key].voucherGarantiaUrl = r.voucherGarantiaUrl;
        reservasAgrupadasMap[key].estadoGarantia = r.estadoGarantia;
      }
    }
  });

  const ordenarHorariosCronologicamente = (horarios) => {
    return [...horarios].sort((a, b) => {
      const hA = parseInt(a.split(':')[0], 10);
      const hB = parseInt(b.split(':')[0], 10);
      const valA = hA < 6 ? hA + 24 : hA;
      const valB = hB < 6 ? hB + 24 : hB;
      return valA - valB;
    });
  };

  const listaReservasAgrupadas = Object.values(reservasAgrupadasMap).map(grupo => {
    const horariosOrdenados = ordenarHorariosCronologicamente(grupo.horarios);
    let horarioMostrado = '';
    
    if (horariosOrdenados.length === 1) {
      horarioMostrado = horariosOrdenados[0];
    } else {
      const primerHorario = horariosOrdenados[0];
      const ultimoHorario = horariosOrdenados[horariosOrdenados.length - 1];
      const partesPrimero = primerHorario.split('-');
      const partesUltimo = ultimoHorario.split('-');
      if (partesPrimero.length === 2 && partesUltimo.length === 2) {
        horarioMostrado = `${partesPrimero[0].trim()} - ${partesUltimo[1].trim()}`;
      } else {
        horarioMostrado = `${primerHorario} al ${ultimoHorario}`;
      }
    }
    return {
      ...grupo,
      horarioMostrado
    };
  });

  const formatearHoraTarjeta = (horaStr) => {
    if (!horaStr) return '';
    if (horaStr.includes(',')) {
      const partes = horaStr.split(',').map(h => h.trim());
      if (partes.length === 1) return partes[0];
      const primerSub = partes[0].split('-');
      const ultimoSub = partes[partes.length - 1].split('-');
      if (primerSub.length === 2 && ultimoSub.length === 2) {
        return `${primerSub[0].trim()} - ${ultimoSub[1].trim()}`;
      }
    }
    return horaStr;
  };

  const formatearDiasTarjeta = (diasText) => {
    if (!diasText) return '';
    if (diasText.toLowerCase().includes(' a ') || diasText.includes('-')) {
      return diasText;
    }

    const diasOrden = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
    const normalizarDia = (d) => {
      const dLower = d.trim().toLowerCase();
      if (dLower === 'miercoles') return 'miércoles';
      if (dLower === 'sabado') return 'sábado';
      return dLower;
    };

    const partes = diasText.split(',').map(d => normalizarDia(d));
    if (partes.length <= 1) return diasText;

    const indices = partes.map(d => diasOrden.indexOf(d)).filter(idx => idx !== -1);
    if (indices.length !== partes.length) return diasText;

    indices.sort((a, b) => a - b);

    let esContinuo = true;
    for (let i = 0; i < indices.length - 1; i++) {
      if (indices[i + 1] !== indices[i] + 1) {
        esContinuo = false;
        break;
      }
    }

    const capitalizar = (str) => str.charAt(0).toUpperCase() + str.slice(1);

    if (esContinuo && indices.length > 1) {
      const primerDia = capitalizar(diasOrden[indices[0]]);
      const ultimoDia = capitalizar(diasOrden[indices[indices.length - 1]]);
      return `${primerDia} a ${ultimoDia}`;
    }

    return partes.map(p => capitalizar(p)).join(', ');
  };

  const formatearPrecioSeguro = (valor, tipo = 'costo') => {
    if (!valor) return tipo === 'costo' ? 'Gratis' : 'Sin garantía';
    const limpio = String(valor).replace(/S\/\s*/gi, '').trim();
    if (!limpio || limpio === '0' || limpio === '0.00') {
      return tipo === 'costo' ? 'Gratis' : 'Sin garantía';
    }
    return `S/ ${limpio}`;
  };

  return (
    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)', boxSizing: 'border-box', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      
      {/* Encabezado Principal */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <div style={{ backgroundColor: '#e0f2fe', color: '#0284c7', width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', boxShadow: '0 4px 6px -1px rgba(2, 132, 199, 0.2)', flexShrink: 0 }}>
          🗓️
        </div>
        <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.25rem', fontWeight: '800', wordBreak: 'break-word' }}>
          Reserva de Áreas Sociales
        </h3>
      </div>
      <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px', lineHeight: '1.5' }}>
        Consulta las áreas disponibles, revisa sus normativas y realiza tus solicitudes de reserva de forma rápida.
      </p>
      
      {notaImportante && (
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '15px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px', marginBottom: '8px' }}>
            <h4 style={{ margin: 0, color: '#92400e', fontSize: '0.95rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>📌</span> Notas Importantes para Reservas
            </h4>
            {notaImportantePdfUrl && (
              <button 
                type="button"
                onClick={() => abrirArchivoConLimiteGlobal(notaImportantePdfUrl, 'nota_importante_pdf_prop')}
                style={{ backgroundColor: '#d97706', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', boxShadow: '0 2px 4px rgba(217, 119, 6, 0.2)', whiteSpace: 'nowrap' }}
              >
                <span>📄</span> IMPORTANTE PDF Informativo ↗
              </button>
            )}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#b45309', whiteSpace: 'pre-line', lineHeight: '1.6', wordBreak: 'break-word' }}>
            {notaImportante}
          </div>
        </div>
      )}

      {areasSociales.length === 0 ? (
        <div style={{ backgroundColor: '#fef2f2', border: '1px dashed #fecaca', borderRadius: '12px', padding: '30px 15px', textAlign: 'center', color: '#991b1b', margin: '20px 0' }}>
          <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '10px' }}>⚠️</span>
          <p style={{ fontSize: '1.05rem', fontWeight: 'bold', margin: '0 0 6px 0' }}>No hay áreas sociales habilitadas</p>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#b91c1c' }}>La administración aún no ha configurado ni creado áreas sociales para este edificio.</p>
        </div>
      ) : (
        <>
          {/* 📋 TARJETAS DE ÁREAS SOCIALES */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '15px', marginBottom: '25px' }}>
            {areasSocialesOrdenadas.map(area => (
              <div key={area.id} style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 2px 4px rgba(0,0,0,0.01)', boxSizing: 'border-box', overflow: 'hidden' }}>
                <div>
                  <h4 style={{ margin: '0 0 10px 0', color: '#0f172a', fontSize: '1rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', wordBreak: 'break-word' }}>
                    <span>🏡</span> {area.nombre}
                    {area.bloqueoDiaCompleto && (
                      <span title="Bloqueo de día completo activado" style={{ fontSize: '0.8rem', cursor: 'help' }}>🔒</span>
                    )}
                  </h4>
                  
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <span style={{ fontSize: '0.75rem', backgroundColor: '#e0f2fe', color: '#0369a1', padding: '4px 8px', borderRadius: '20px', fontWeight: '700' }}>
                      Costo: {formatearPrecioSeguro(area.costo, 'costo')}
                    </span>
                    <span style={{ fontSize: '0.75rem', backgroundColor: '#fef3c7', color: '#b45309', padding: '4px 8px', borderRadius: '20px', fontWeight: '700' }}>
                      Garantía: {formatearPrecioSeguro(area.garantia, 'garantia')}
                    </span>
                  </div>

                  <p style={{ margin: '0 0 10px 0', fontSize: '0.82rem', color: '#475569', wordBreak: 'break-word' }}>
                    🧹 Limpieza: <strong>{area.limpieza === 'Libre' ? 'Libre (A elección del propietario)' : (area.limpieza || 'Limpieza incluida')}</strong>
                  </p>
                  
                  <div style={{ margin: '0 0 10px 0' }}>
                    <p style={{ margin: '0 0 6px 0', fontSize: '0.78rem', color: '#334155', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🕒 Horarios Disponibles:</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {area.horariosPorDia && area.horariosPorDia.length > 0 ? (
                        area.horariosPorDia.map((bloque, i) => (
                          <div key={i} style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', color: '#334155', padding: '6px 8px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '600', wordBreak: 'break-word' }}>
                            📅 {formatearDiasTarjeta(bloque.diasText)}: <span style={{ color: '#0284c7' }}>⏰ {formatearHoraTarjeta(bloque.hora)}</span>
                          </div>
                        ))
                      ) : (
                        <span style={{ fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic' }}>Horario estándar general</span>
                      )}
                    </div>
                  </div>
                </div>

                {area.reglas ? (
                  <div style={{ margin: '8px 0 0 0', fontSize: '0.78rem', color: '#64748b', borderTop: '1px solid #e2e8f0', paddingTop: '8px', wordBreak: 'break-word' }}>
                    <strong style={{ color: '#334155' }}>Normas de uso:</strong>
                    <ol style={{ margin: '4px 0 0 15px', paddingLeft: '0' }}>
                      {area.reglas.split('\n').map((regla, rIdx) => {
                        const reglaLimpia = regla.replace(/^\d+[.)]\s*/, '').trim();
                        return reglaLimpia ? <li key={rIdx} style={{ marginBottom: '2px' }}>{reglaLimpia}</li> : null;
                      })}
                    </ol>
                  </div>
                ) : (
                  <p style={{ margin: '8px 0 0 0', fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>Sin reglas adicionales especificadas.</p>
                )}
              </div>
            ))}
          </div>

          {/* Formulario de Reserva */}
          <form onSubmit={manejarEnvioReserva} style={{ display: 'flex', flexDirection: 'column', gap: '15px', backgroundColor: '#f0fdf4', padding: '20px', borderRadius: '14px', border: '1px solid #bbf7d0', marginBottom: '25px', boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.02)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>📝</span>
              <h4 style={{ margin: 0, color: '#166534', fontSize: '1.1rem', fontWeight: '800', wordBreak: 'break-word' }}>Formulario de Solicitud de Reserva</h4>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
              <select 
                value={formReserva.areaId} 
                onChange={e => { setFormReserva({...formReserva, areaId: e.target.value, fecha: ''}); setHorariosSeleccionados([]); }} 
                style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: 'white', fontWeight: '600', color: '#1e293b', outline: 'none', fontSize: '0.9rem', cursor: 'pointer', boxSizing: 'border-box' }} 
                required
              >
                <option value="">Selecciona Área Social</option>
                {areasSocialesOrdenadas.map(a => {
                  const cNum = obtenerValorNumerico(a.costo);
                  const gNum = obtenerValorNumerico(a.garantia);
                  
                  let detalleMontos = 'Gratis';
                  if (cNum > 0 && gNum > 0) {
                    detalleMontos = `Costo: S/ ${cNum.toFixed(2)} | Garantía: S/ ${gNum.toFixed(2)}`;
                  } else if (cNum > 0) {
                    detalleMontos = `Costo: S/ ${cNum.toFixed(2)}`;
                  } else if (gNum > 0) {
                    detalleMontos = `Garantía: S/ ${gNum.toFixed(2)}`;
                  }

                  return (
                    <option key={a.id} value={a.id}>
                      {a.nombre} ({detalleMontos})
                    </option>
                  );
                })}
              </select>

              <div>
                <input 
                  type="date" 
                  value={formReserva.fecha} 
                  min={new Date().toISOString().split('T')[0]} 
                  max={fechaMaximaStr}
                  onChange={manejarCambioFecha} 
                  style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: 'white', fontWeight: '600', color: '#1e293b', outline: 'none', fontSize: '0.9rem', boxSizing: 'border-box' }} 
                  required 
                />
                {plazoAnticipacionMeses && (
                  <span style={{ fontSize: '0.75rem', color: '#15803d', marginTop: '4px', display: 'block', fontWeight: '600' }}>
                    ⏳ Plazo máximo de anticipación: {plazoAnticipacionMeses} {Number(plazoAnticipacionMeses) === 1 ? 'mes' : 'meses'} ({plazoAnticipacionMeses * 30} días).
                  </span>
                )}
              </div>
            </div>

            {/* 🧹 SECCIÓN CONDICIÓN DE LIMPIEZA DINÁMICA SI ES "Libre" */}
            {areaSeleccionadaObj?.limpieza === 'Libre' && (
              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #cbd5e1', boxShadow: '0 2px 4px rgba(0,0,0,0.01)', boxSizing: 'border-box' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '700', display: 'block', marginBottom: '8px', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  🧹 ¿Cómo se manejará la limpieza de esta área? (Obligatorio)
                </label>
                <select 
                  value={tipoLimpiezaElegida}
                  onChange={e => setTipoLimpiezaElegida(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', fontWeight: '600', color: '#1e293b', marginBottom: '12px', outline: 'none' }}
                >
                  <option value="Lo realiza el propietario">Lo realiza el propietario</option>
                  <option value="Lo realiza la administración">Lo realiza la administración</option>
                  <option value="Limpieza externa contratada">El propietario contrata una limpieza externa</option>
                </select>

                {tipoLimpiezaElegida === 'Limpieza externa contratada' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginTop: '10px', backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#166534', display: 'block', marginBottom: '4px' }}>Nombre completo de la persona:</label>
                      <input 
                        type="text" 
                        placeholder="Ej. María Pérez"
                        value={nombreLimpiezaExterna}
                        onChange={e => setNombreLimpiezaExterna(e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: 'white', boxSizing: 'border-box', fontSize: '0.85rem' }}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#166534', display: 'block', marginBottom: '4px' }}>DNI de la persona:</label>
                      <input 
                        type="text" 
                        placeholder="Ej. 12345678"
                        value={dniLimpiezaExterna}
                        onChange={e => setDniLimpiezaExterna(e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: 'white', boxSizing: 'border-box', fontSize: '0.85rem' }}
                        required
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ⏰ SECCIÓN DE SELECCIÓN DE HORARIOS (Muestra las horas, pero bloquea todo el día si la función está activa y ya hay reservas) */}
            {formReserva.areaId && formReserva.fecha && (
              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #cbd5e1', boxShadow: '0 2px 4px rgba(0,0,0,0.01)', boxSizing: 'border-box' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '700', display: 'block', marginBottom: '10px', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  ⏰ Selecciona el horario correspondiente para la fecha elegida:
                </label>

                {estaFechaTotalmenteBloqueada ? (
                  <div style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '12px 14px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '700', border: '1px solid #fecaca' }}>
                    ❌ Lo sentimos, esta fecha ya se encuentra totalmente reservada.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
                    {horasDisponiblesArea.map((h, i) => {
                      const estaReservado = horariosOcupados.includes(h);
                      const estaEnRango = indicesEfectivos.includes(i);

                      return (
                        <label 
                          key={i} 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            fontSize: '0.85rem', 
                            color: estaReservado ? '#94a3b8' : '#0f172a', 
                            cursor: estaReservado ? 'not-allowed' : 'pointer',
                            backgroundColor: estaEnRango ? '#e0f2fe' : '#f8fafc',
                            border: estaEnRango ? '1px solid #0284c7' : '1px solid #e2e8f0',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            fontWeight: estaEnRango ? '700' : '500',
                            transition: 'all 0.15s',
                            boxSizing: 'border-box',
                            wordBreak: 'break-word'
                          }}
                        >
                          <input 
                            type="checkbox" 
                            value={h}
                            checked={estaEnRango}
                            disabled={estaReservado}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setHorariosSeleccionados([...horariosSeleccionados, h]);
                              } else {
                                setHorariosSeleccionados(horariosSeleccionados.filter(item => item !== h));
                              }
                            }}
                            style={{ width: '16px', height: '16px', cursor: estaReservado ? 'not-allowed' : 'pointer', accentColor: '#0284c7', flexShrink: 0 }}
                          />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{h} {estaReservado ? '🔒' : ''}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 💳 VOUCHER */}
            {formReserva.areaId && (
              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: esDePago ? '1px dashed #16a34a' : '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '700', display: 'block', marginBottom: '8px', color: esDePago ? '#166534' : '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', wordBreak: 'break-word' }}>
                  {esDePago 
                    ? `💳 Área sujeta a pagos (${costoNum > 0 ? `Costo: S/ ${costoNum.toFixed(2)}` : ''} ${costoNum > 0 && garantiaNum > 0 ? 'y ' : ''} ${garantiaNum > 0 ? `Garantía: S/ ${garantiaNum.toFixed(2)}` : ''}). Adjunta tu voucher (Obligatorio):`
                    : '💳 Área gratuita y sin garantía. Comprobante opcional:'}
                </label>
                <input 
                  type="file" 
                  accept=".pdf, .jpg, .jpeg, .png" 
                  onChange={e => setArchivoVoucherReserva(e.target.files[0])} 
                  required={esDePago} 
                  style={{ width: '100%', fontSize: '0.85rem', color: '#475569', boxSizing: 'border-box' }}
                />
                {archivoVoucherReserva && (
                  <p style={{ fontSize: '0.8rem', color: '#0284c7', margin: '8px 0 0 0', fontWeight: '600', wordBreak: 'break-word' }}>Archivo seleccionado: {archivoVoucherReserva.name}</p>
                )}
              </div>
            )}

            {/* 📜 DECLARACIÓN JURADA */}
            {declaracionJuradaUrl && (
              <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <span style={{ fontSize: '0.85rem', color: '#1e40af', fontWeight: '700', wordBreak: 'break-word', flex: 1, minWidth: '180px' }}>
                    📜 Es obligatorio leer y aceptar la Declaración Jurada para reservar.
                  </span>
                  <button 
                    type="button"
                    onClick={() => abrirArchivoConLimiteGlobal(declaracionJuradaUrl, 'declaracion_jurada_prop')}
                    style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 4px rgba(2, 132, 199, 0.2)', whiteSpace: 'nowrap' }}
                  >
                    📖 Leer Declaración Jurada ↗
                  </button>
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '0.85rem', color: '#1e3a8a', cursor: 'pointer', fontWeight: '700', wordBreak: 'break-word' }}>
                  <input 
                    type="checkbox" 
                    checked={aceptoDeclaracion} 
                    onChange={e => setAceptoDeclaracion(e.target.checked)} 
                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#0284c7', marginTop: '2px', flexShrink: 0 }}
                    required 
                  />
                  <span>He leído y acepto los términos de la Declaración Jurada y las normas de uso de las áreas sociales.</span>
                </label>
              </div>
            )}

            <div style={{ textAlign: 'right', width: '100%' }}>
              <button 
                type="submit" 
                disabled={subiendoVoucherReserva || estaFechaTotalmenteBloqueada} 
                style={{ width: '100%', backgroundColor: estaFechaTotalmenteBloqueada ? '#94a3b8' : '#10b981', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '10px', fontWeight: 'bold', cursor: estaFechaTotalmenteBloqueada ? 'not-allowed' : 'pointer', fontSize: '0.95rem', boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)', transition: 'background-color 0.2s', boxSizing: 'border-box' }}
              >
                {subiendoVoucherReserva ? '⏳ Procesando reservas y voucher...' : 'Confirmar y Enviar Reservas 🚀'}
              </button>
            </div>
          </form>
        </>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', marginTop: '10px' }}>
        <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>📋</span>
        <h4 style={{ color: '#0f172a', margin: 0, fontSize: '1.1rem', fontWeight: '700', wordBreak: 'break-word' }}>Mis Reservas Realizadas</h4>
      </div>

      {/* Contenedor con scroll horizontal fluido para tablas en celulares */}
      <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px', border: '1px solid #e2e8f0', boxSizing: 'border-box' }}>
        <table className="tabla-propietarios" style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#334155', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
              <th style={{ padding: '12px 14px' }}>Área</th>
              <th style={{ padding: '12px 14px' }}>Departamento</th>
              <th style={{ padding: '12px 14px' }}>Fecha</th>
              <th style={{ padding: '12px 14px' }}>Horario</th>
              <th style={{ padding: '12px 14px' }}>Limpieza Elegida</th>
              <th style={{ padding: '12px 14px' }}>Voucher / Estado</th>
              <th style={{ padding: '12px 14px' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {listaReservasAgrupadas.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '30px 15px', color: '#64748b', fontStyle: 'italic', backgroundColor: '#ffffff' }}>
                  No tienes reservas registradas con este departamento.
                </td>
              </tr>
            ) : (
              listaReservasAgrupadas.map((grupo, idx) => {
                const area = areasSociales.find(a => a.id === grupo.areaId);
                const garantiaRequerida = obtenerValorNumerico(area?.garantia);

                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa', transition: 'background-color 0.15s' }}>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: '#0f172a' }}>{area ? area.nombre : 'Área'}</td>
                    <td style={{ padding: '12px 14px', color: '#64748b' }}>Dpto. {usuarioLogueado?.departamento?.departamento || ''}</td>
                    <td style={{ padding: '12px 14px', color: '#0f172a', fontWeight: '600', whiteSpace: 'nowrap' }}>{grupo.fecha}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ backgroundColor: '#fee2e2', color: '#dc2626', padding: '4px 8px', borderRadius: '20px', fontWeight: '700', fontSize: '0.75rem', display: 'inline-block', border: '1px solid #fecaca', whiteSpace: 'nowrap' }}>
                        🔒 {grupo.horarioMostrado}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '0.85rem', color: '#334155' }}>
                      {grupo.tipoLimpieza === 'Limpieza externa contratada' ? (
                        <div>
                          <strong style={{ display: 'block', color: '#166534' }}>Externa contratada:</strong>
                          <span>{grupo.nombreLimpiezaExterna}</span><br />
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>DNI: {grupo.dniLimpiezaExterna}</span>
                        </div>
                      ) : (
                        <span>{grupo.tipoLimpieza || area?.limpieza || 'Incluida'}</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {/* Voucher Alquiler / Costo */}
                        <div>
                          <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 'bold', display: 'block' }}>PAGO / ALQUILER:</span>
                          {grupo.voucherUrl ? (
                            <button 
                              type="button"
                              onClick={() => abrirArchivoConLimiteGlobal(grupo.voucherUrl, `${grupo.ids[0]}_v`)}
                              style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '5px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', boxShadow: '0 2px 4px rgba(2, 132, 199, 0.2)', whiteSpace: 'nowrap' }}
                            >
                              <span>👁️</span> Ver Voucher Alquiler
                            </button>
                          ) : (
                            <span style={{ color: '#16a34a', fontSize: '0.75rem', fontWeight: '700', backgroundColor: '#dcfce7', padding: '3px 6px', borderRadius: '12px', border: '1px solid #bbf7d0', display: 'inline-block', whiteSpace: 'nowrap' }}>Gratis / Sin costo</span>
                          )}
                        </div>

                        {/* Voucher Garantía (Si el área aplica garantía) */}
                        {garantiaRequerida > 0 && (
                          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '6px' }}>
                            <span style={{ fontSize: '0.7rem', color: '#b45309', fontWeight: 'bold', display: 'block' }}>GARANTÍA (S/ {garantiaRequerida.toFixed(2)}):</span>
                            {grupo.voucherGarantiaUrl ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <button 
                                  type="button"
                                  onClick={() => abrirArchivoConLimiteGlobal(grupo.voucherGarantiaUrl, `${grupo.ids[0]}_vg`)}
                                  style={{ backgroundColor: '#d97706', color: 'white', border: 'none', padding: '5px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', boxShadow: '0 2px 4px rgba(217, 119, 6, 0.2)', whiteSpace: 'nowrap' }}
                                >
                                  <span>👁️</span> Ver Voucher Garantía
                                </button>
                                <span style={{ fontSize: '0.7rem', color: '#d97706', fontWeight: '600' }}>({grupo.estadoGarantia || 'En revisión'})</span>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '0.68rem', color: '#64748b', fontStyle: 'italic', lineHeight: '1.2' }}>
                                  💡 Sube este voucher solo si no pagaste la garantía junto con tu reserva inicial.
                                </span>
                                <input 
                                  type="file" 
                                  accept=".pdf, .jpg, .jpeg, .png" 
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    setArchivosGarantia(prev => ({ ...prev, [grupo.key]: file }));
                                  }}
                                  style={{ fontSize: '0.75rem', width: '100%', maxWidth: '180px' }}
                                />
                                {archivosGarantia[grupo.key] && (
                                  <button 
                                    type="button"
                                    onClick={() => manejarSubirVoucherGarantia(grupo)}
                                    style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 'bold', cursor: 'pointer', alignSelf: 'flex-start' }}
                                  >
                                    🚀 Subir Garantía
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <button 
                        type="button"
                        onClick={() => cancelarReservaGrupo(grupo.ids)}
                        style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)', whiteSpace: 'nowrap' }}
                      >
                        <span>❌</span> Cancelar
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}